import { useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { gql } from 'graphql-tag';
import { useHasFeatureFlag } from '~/modules/common/hooks';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';
import { QUERY_LIMIT } from './useResourceAllocationsSummary';
import { mapProjectStatusToProjectWorkflowStages } from './utils';

export const RESOURCE_ACTUAL_SERIES_FOR_USERS_QUERY = gql`
  query getResourceUserActualSeriesForProjectResources(
    $dateRange: DateRangeInput!
    $periodResolution: PeriodResolutionOption!
    $userUri: String!
    $cursor: String
    $limit: Int
    $sort: ResourceAllocationSort
    $projectWorkflowStages: [ProjectWorkflowStage!]
  ) {
    resourceUserProjectActualSeriesForUser(
      dateRange: $dateRange
      periodResolution: $periodResolution
      userUri: $userUri
      cursor: $cursor
      limit: $limit
      sort: $sort
      projectWorkflowStages: $projectWorkflowStages
    ) {
      actualEntries {
        user {
          id
          displayText
        }
        project {
          id
          displayText
        }
        role {
          id
          displayText
        }
        periods {
          emptyPeriodInFuture
          hours {
            hours
            minutes
            seconds
          }
          cost {
            amount
            currency {
              id
              uri
              displayText
              symbol
            }
          }
          dateRange {
            startDate: startDate2
            endDate: endDate2
          }
        }
      }
      nextPageCursor
    }
  }
`;

export const useResourceProjectActualSeriesForUser = ({
  dateRange,
  scale,
  userUri,
  sort,
  cursor,
  limit = QUERY_LIMIT,
  projectStatusFilterOption,
  isResourceActualModeEnabled = false
}) => {
  const isPSARMPResourceActualEnabled = useHasFeatureFlag({
    featureFlag: 'isPSARMPResourceActualEnabled'
  });
  const periodResolution = mapScaleToPeriodResolution(scale);
  const [loadingMore, setLoadingMore] = useState(false);
  const variables = useMemo(
    () => ({
      dateRange,
      periodResolution,
      userUri,
      cursor,
      limit,
      sort,
      projectWorkflowStages: mapProjectStatusToProjectWorkflowStages(
        projectStatusFilterOption
      )
    }),
    [
      limit,
      dateRange,
      periodResolution,
      userUri,
      cursor,
      sort,
      projectStatusFilterOption
    ]
  );

  const { data, loading, fetchMore } = useQuery(
    RESOURCE_ACTUAL_SERIES_FOR_USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables,
      skip: !(isPSARMPResourceActualEnabled && isResourceActualModeEnabled)
    }
  );

  const resourceUserActualSeries =
    data?.resourceUserProjectActualSeriesForUser?.actualEntries || [];

  const nextPageCursor =
    data?.resourceUserProjectActualSeriesForUser?.nextPageCursor;

  const hasMoreRows = Boolean(nextPageCursor);

  const loadMoreRows = useCallback(async () => {
    if (loading || loadingMore || !hasMoreRows) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        query: RESOURCE_ACTUAL_SERIES_FOR_USERS_QUERY,
        variables: {
          ...variables,
          cursor: nextPageCursor
        }
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMoreRows, fetchMore, variables, nextPageCursor]);

  return {
    isLoading: loading,
    loadMoreActualRows: loadMoreRows,
    resourceUserActualSeriesByUser: resourceUserActualSeries,
    cursor: nextPageCursor
  };
};

export default useResourceProjectActualSeriesForUser;
